import React, { useEffect, useRef, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { t as tr } from '../../i18n';
import { QUICK_REACTIONS, ReactionSummary } from '../../utils/chatReactions';
import { emojiCategoriesForPicker, loadRecentEmojis, rememberRecentEmoji } from '../../utils/emojiCatalog';

const TEAL = '#3fced1';

/**
 * Reaktionen an einer Chat-Nachricht (Marcel 22.10.2026, „wie bei WhatsApp“):
 *  - Leiste mit den sechs Schnellreaktionen + „mehr“ (eigener Emoji-Picker), geöffnet über den Smiley an der Blase
 *    (Desktop: sichtbar beim Darüberfahren, Touch: immer) oder langes Drücken / Rechtsklick auf die Blase,
 *  - Chips unter der Blase mit Zähler; die eigene Reaktion ist türkis umrandet, Klick auf einen Chip wiederholt sie
 *    (gleiche → weg, andere → ersetzt – eine Reaktion je Nutzer, Regel im Backend).
 */
export const ReactionChips: React.FC<{ summary: ReactionSummary[] | null | undefined; isOwn: boolean; onReact: (emoji: string) => void; testId?: string }> = ({ summary, isOwn, onReact, testId = 'reaction-chips' }) => {
  const list = Array.isArray(summary) ? summary.filter((s) => s && s.emoji && s.count > 0) : [];
  if (list.length === 0) return null;
  return (
    <div className={`-mt-2 mb-1 flex flex-wrap gap-1 ${isOwn ? 'justify-end pr-2' : 'justify-start pl-2'}`} data-testid={testId}>
      {list.map((s) => (
        <button
          key={s.emoji}
          type="button"
          onClick={() => onReact(s.emoji)}
          className={`inline-flex items-center gap-1 rounded-full border bg-zinc-900 px-2 py-0.5 text-xs text-zinc-200 shadow-sm transition hover:border-[#3fced1] ${s.mine ? 'border-[#3fced1]' : 'border-zinc-700'}`}
          aria-pressed={s.mine}
          aria-label={tr('web.component.message-reactions.reaktion-aria', { emoji: s.emoji, count: String(s.count) })}
          data-testid={`${testId}-${s.emoji}`}
        >
          <span>{s.emoji}</span>
          {s.count > 1 && <span className="font-semibold">{s.count}</span>}
        </button>
      ))}
    </div>
  );
};

/** Leiste mit Schnellreaktionen + „mehr“; schließt bei Klick außerhalb und Escape. */
export const ReactionBar: React.FC<{ open: boolean; isOwn: boolean; onPick: (emoji: string) => void; onClose: () => void; testId?: string }> = ({ open, isOwn, onPick, onClose, testId = 'reaction-bar' }) => {
  const [more, setMore] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) { setMore(false); return undefined; }
    const onDoc = (e: MouseEvent | TouchEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('touchstart', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, onClose]);
  if (!open) return null;
  const pick = (emoji: string) => { rememberRecentEmoji(emoji); onPick(emoji); onClose(); };
  const categories = emojiCategoriesForPicker(loadRecentEmojis());
  return (
    <div ref={ref} className={`absolute z-[70] -top-11 ${isOwn ? 'right-0' : 'left-0'}`} data-testid={testId} role="toolbar" aria-label={tr('web.component.message-reactions.reagieren')}>
      <div className="flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900 px-2 py-1 shadow-2xl">
        {QUICK_REACTIONS.map((emoji) => (
          <button key={emoji} type="button" onClick={() => pick(emoji)} className="rounded-full px-1.5 py-1 text-xl leading-none transition hover:scale-125" data-testid={`${testId}-${emoji}`} aria-label={emoji}>{emoji}</button>
        ))}
        <button type="button" onClick={() => setMore((v) => !v)} className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-800 text-zinc-300 hover:text-white" aria-label={tr('web.component.message-reactions.mehr')} data-testid={`${testId}-more`} aria-expanded={more}>
          <SmilePlus size={16} />
        </button>
      </div>
      {more && (
        <div className={`absolute top-11 ${isOwn ? 'right-0' : 'left-0'} max-h-64 w-[300px] max-w-[calc(100vw-32px)] overflow-y-auto rounded-2xl border border-zinc-700 bg-black p-2 shadow-2xl`} data-testid={`${testId}-picker`}>
          {categories.map((cat) => (
            <div key={cat.key} className="mb-2">
              <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{cat.label}</p>
              <div className="grid grid-cols-8 gap-0.5">
                {cat.emojis.map((emoji) => (
                  <button key={emoji} type="button" onClick={() => pick(emoji)} className="rounded-md py-1 text-lg leading-none hover:bg-zinc-800" aria-label={emoji}>{emoji}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Smiley an der Blase, der die Leiste öffnet (Desktop beim Darüberfahren, Touch immer sichtbar). */
export const ReactButton: React.FC<{ onClick: () => void; isOwn: boolean; alwaysVisible: boolean; testId?: string }> = ({ onClick, isOwn, alwaysVisible, testId = 'react-button' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`self-center rounded-full p-1 text-zinc-500 transition hover:text-white ${alwaysVisible ? 'opacity-70' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'} ${isOwn ? 'mr-1' : 'ml-1'}`}
    aria-label={tr('web.component.message-reactions.reagieren')}
    data-testid={testId}
    style={{ color: undefined }}
  >
    <SmilePlus size={16} color={TEAL} />
  </button>
);

export default ReactionChips;
